<script setup lang="ts">
import FaqItem from './FaqItem.vue';

export type ItemType = {
    title?: string;
    value?: string;
    noSchema?: boolean;
};

interface IProps {
    items?: ItemType[];
    limit?: number;
    compactTitle?: boolean;
    noSchema?: boolean;
    highlight?: string;
}
const { items = [], limit = 0, noSchema } = defineProps<IProps>();
if (!noSchema && items.length) {
    useSchemaOrg([
        defineWebPage({
            '@type': 'FAQPage',
            '@context': 'https://schema.org',
        }),
        ...(items
            .filter(i => !i.noSchema)
            .map(i =>
                defineQuestion({
                    '@type': 'Question',
                    name: i.title,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: i.value ?? '',
                    },
                })
            ) || []),
    ]);
}
const activeIndex = ref<string>();
const toggle = (id: string) => {
    activeIndex.value = activeIndex.value === id ? undefined : id;
};
const hasMore = computed(() => {
    if (!limit) {
        return false;
    }
    return items.length > limit;
});
const shownItems = computed(() => items.slice(0, limit || items.length));
const hiddenItems = computed(() => items.slice(limit));
</script>

<template>
    <mr-more :disabled="!hasMore">
        <template #default="{ expand }">
            <FaqItem
                v-for="(faq, j) in shownItems"
                :key="j"
                :expanded="activeIndex === `accordion-${j}`"
                :faq
                :highlight
                @toggle="toggle(`accordion-${j}`)"
            >
                <template #separator>
                    <div
                        v-show="
                            j !== shownItems.length - 1 ||
                            (hiddenItems.length && expand)
                        "
                        class="line"
                    />
                </template>
            </FaqItem>
        </template>
        <template #more>
            <FaqItem
                v-for="(faq, j) in hiddenItems"
                :key="j + limit"
                :expanded="activeIndex === `accordion-${j + limit}`"
                :faq
                :highlight
                @toggle="toggle(`accordion-${j + limit}`)"
            >
                <template #separator>
                    <div v-show="j !== hiddenItems.length - 1" class="line" />
                </template>
            </FaqItem>
        </template>
    </mr-more>
</template>

<style scoped lang="scss">
.line {
    width: 100%;
    height: 1px;
    background-color: usePalette($p-cool-gray, $p-100);
}
</style>
